import React, { useState, useEffect, useCallback, useRef } from 'react';
import { structureAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { ZoomIn, ZoomOut, Maximize2, User, Globe } from 'lucide-react';
import RoleDetailDrawer from './RoleDetailDrawer';

// ========================
// STYLING CONSTANTS
// ========================

// National Chart Tier Colors
const NATIONAL_TIER_STYLES = {
  1: { headerBg: '#E8E4DC', headerText: '#5C5852', nodeBg: '#F5F3EF', nodeText: '#5C5852', nodeBorder: '#D4D0C8' },
  2: { headerBg: '#1E3A5F', headerText: '#FFFFFF', nodeBg: '#2C5282', nodeText: '#FFFFFF', nodeBorder: '#1E3A5F' },
  3: { headerBg: '#4A6FA5', headerText: '#FFFFFF', nodeBg: '#5B8DC9', nodeText: '#FFFFFF', nodeBorder: '#4A6FA5' },
  4: { headerBg: '#319795', headerText: '#FFFFFF', nodeBg: '#38B2AC', nodeText: '#FFFFFF', nodeBorder: '#2C7A7B' }
};

// UNIQUE IP Chart Styles
const IP_STYLES = {
  board: { bg: '#319795', text: '#FFFFFF', border: '#2C7A7B' },
  csuite: { bg: '#319795', text: '#FFFFFF', border: '#2C7A7B' },
  execDirector: { bg: '#1D4E4C', text: '#FFFFFF', border: '#1D4E4C' },
  director: { bg: '#E5E7EB', text: '#374151', border: '#D1D5DB' },
  head: { bg: '#6B7280', text: '#FFFFFF', border: '#4B5563' },
  lead: { bg: '#9CA3AF', text: '#1F2937', border: '#6B7280' },
  teamMember: { bg: '#E5E7EB', text: '#374151', border: '#D1D5DB' },
  coordinator: { bg: '#9CA3AF', text: '#1F2937', border: '#6B7280' },
  vacant: { bg: '#FEF3C7', text: '#92400E' }
};

// ========================
// SHARED COMPONENTS
// ========================

const RoleNode = ({ role, style, onClick, size = 'normal' }) => {
  const isVacant = !role.assignedPersonId;
  const width = size === 'small' ? 'w-[130px]' : size === 'large' ? 'w-[200px]' : 'w-[160px]';
  
  return (
    <div
      className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${width}`}
      onClick={() => onClick(role)}
      data-testid={`org-node-${role.id}`}
    >
      <div
        className="rounded-lg shadow-md overflow-hidden"
        style={{ backgroundColor: style.bg, border: `2px solid ${style.border || style.bg}` }}
      >
        <div className="px-3 py-2 text-center">
          <h3 className="font-semibold text-xs leading-tight" style={{ color: style.text }}>
            {role.title}
          </h3>
        </div>
        <div 
          className="px-3 py-1.5 text-center"
          style={{ backgroundColor: isVacant ? IP_STYLES.vacant.bg : 'rgba(255,255,255,0.95)' }}
        >
          {isVacant ? (
            <span className="text-xs font-medium" style={{ color: IP_STYLES.vacant.text }}>Vacant</span>
          ) : (
            <span className="text-xs font-medium" style={{ color: '#374151' }}>
              {role.assignedPerson?.displayName || 'Assigned'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const TierRow = ({ tier, onClick }) => {
  const tierStyle = NATIONAL_TIER_STYLES[tier.tierLevel] || NATIONAL_TIER_STYLES[1];
  
  return (
    <div className="mb-6" data-testid={`tier-${tier.tierLevel}`}>
      <div className="flex justify-center mb-4">
        <div className="px-6 py-2 rounded-full shadow-sm" style={{ backgroundColor: tierStyle.headerBg, color: tierStyle.headerText }}>
          <span className="text-sm font-semibold tracking-wide uppercase">{tier.tierGroup}</span>
        </div>
      </div>
      <div className="flex justify-center gap-4 flex-wrap">
        {tier.roles.map((role) => (
          <RoleNode key={role.id} role={role} style={tierStyle} onClick={onClick} />
        ))}
      </div>
      {tier.tierLevel < 4 && (
        <div className="flex justify-center mt-4">
          <div className="w-0.5 h-8" style={{ backgroundColor: '#CBD5E1' }} />
        </div>
      )}
    </div>
  );
};

// ========================
// UNIQUE IP CHART COMPONENTS
// ========================

const IPDepartmentColumn = ({ dept, onClick }) => {
  if (!dept) return null;
  
  const deptStyle = { bg: dept.color || '#6B7280', text: '#FFFFFF', border: dept.color || '#6B7280' };
  
  return (
    <div className="flex flex-col items-center gap-2 min-w-[130px]">
      {/* Department Header */}
      <div className="px-2 py-1.5 rounded text-center w-full" style={{ backgroundColor: '#F3F4F6' }}>
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#374151' }}>
          {dept.name}
        </span>
      </div>
      
      {/* Heads */}
      <div className="flex gap-2 justify-center flex-wrap">
        {dept.heads.map((head) => (
          <RoleNode key={head.id} role={head} style={{ ...IP_STYLES.head, bg: dept.color }} onClick={onClick} size="small" />
        ))}
      </div>
      
      {dept.heads.length > 0 && <div className="w-0.5 h-3" style={{ backgroundColor: '#CBD5E1' }} />}
      
      {/* Leads */}
      <div className="flex gap-2 justify-center flex-wrap">
        {dept.leads.map((lead) => (
          <RoleNode key={lead.id} role={lead} style={IP_STYLES.lead} onClick={onClick} size="small" />
        ))}
      </div>
      
      {dept.leads.length > 0 && <div className="w-0.5 h-3" style={{ backgroundColor: '#CBD5E1' }} />}
      
      {/* Team Members */}
      <div className="flex flex-col gap-1 items-center">
        {dept.teamMembers.map((member) => (
          <RoleNode key={member.id} role={member} style={IP_STYLES.teamMember} onClick={onClick} size="small" />
        ))}
      </div>
    </div>
  );
};

const ChiefOfStaffColumn = ({ cos, onClick }) => {
  if (!cos) return null;
  
  return (
    <div className="flex flex-col items-center gap-2 min-w-[130px]">
      <div className="px-2 py-1.5 rounded text-center w-full" style={{ backgroundColor: '#F3F4F6' }}>
        <span className="text-[10px] font-semibold uppercase tracking-wide" style={{ color: '#374151' }}>
          Chief of Staff
        </span>
      </div>
      
      {/* Coordinators (vertical stack) */}
      <div className="flex flex-col gap-2 items-center">
        {cos.coordinators.map((coord) => (
          <RoleNode key={coord.id} role={coord} style={IP_STYLES.coordinator} onClick={onClick} size="small" />
        ))}
      </div>
    </div>
  );
};

// ========================
// MAIN COMPONENT
// ========================

const OrgChart = ({ departments, people, onUpdate, searchQuery, scopeFilter }) => {
  // If scopeFilter is provided, use it directly; otherwise allow user selection
  const [selectedScope, setSelectedScope] = useState('national');
  const scope = scopeFilter || selectedScope;
  const setScope = scopeFilter ? () => {} : setSelectedScope; // Disable setter if forced
  
  const [nationalData, setNationalData] = useState({ tiers: [] });
  const [uniqueIPData, setUniqueIPData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [zoom, setZoom] = useState(scope === 'unique_ip' ? 0.75 : 1);
  const containerRef = useRef(null);

  const fetchChartData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (scope === 'national') {
        const response = await structureAPI.getTieredChart();
        setNationalData(response.data || { tiers: [] });
      } else if (scope === 'unique_ip') {
        const response = await structureAPI.getUniqueIPChart('soul_food');
        setUniqueIPData(response.data);
      }
    } catch (error) {
      console.error('Error fetching chart:', error);
      setError(error.message || 'Failed to load organization chart');
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    fetchChartData();
    // Adjust zoom based on scope
    setZoom(scope === 'unique_ip' ? 0.7 : 1);
  }, [fetchChartData, scope]);

  const handleNodeClick = useCallback((role) => {
    setSelectedRole(role);
    setDrawerOpen(true);
  }, []);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 1.5));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.4));
  const handleResetZoom = () => setZoom(scope === 'unique_ip' ? 0.7 : 1);

  const handleUpdate = async () => {
    await fetchChartData();
    onUpdate?.();
  };

  // ========================
  // RENDER LOADING/ERROR STATES
  // ========================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[700px] rounded-xl" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#2A9D8F', borderTopColor: 'transparent' }} />
          <p style={{ color: '#6B7280' }}>Loading organization chart...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[700px] rounded-xl" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}>
        <h3 className="text-lg font-medium mb-2" style={{ color: '#991B1B' }}>Failed to load chart</h3>
        <p className="text-sm mb-4" style={{ color: '#B91C1C' }}>{error}</p>
        <Button onClick={fetchChartData} style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}>Try Again</Button>
      </div>
    );
  }

  // ========================
  // RENDER NATIONAL CHART
  // ========================

  const renderNationalChart = () => {
    if (!nationalData.tiers || nationalData.tiers.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <User className="w-16 h-16 mb-4" style={{ color: '#D1D5DB' }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: '#264653' }}>No organization structure yet</h3>
        </div>
      );
    }

    return (
      <>
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold" style={{ color: '#264653' }}>National Organizational Chart</h2>
          <p className="text-sm" style={{ color: '#8D99AE' }}>Company Scope View</p>
        </div>
        {nationalData.tiers.map((tier) => (
          <TierRow key={tier.tierLevel} tier={tier} onClick={handleNodeClick} />
        ))}
      </>
    );
  };

  // ========================
  // RENDER UNIQUE IP CHART
  // ========================

  const renderUniqueIPChart = () => {
    if (!uniqueIPData) {
      return (
        <div className="flex flex-col items-center justify-center h-full">
          <User className="w-16 h-16 mb-4" style={{ color: '#D1D5DB' }} />
          <h3 className="text-lg font-medium mb-2" style={{ color: '#264653' }}>No UNIQUE IP structure yet</h3>
        </div>
      );
    }

    return (
      <>
        {/* Title */}
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold" style={{ color: '#264653' }}>Soul Food Organizational Chart</h2>
          <p className="text-sm" style={{ color: '#8D99AE' }}>UNIQUE IP Scope View</p>
        </div>

        {/* TIER 1: National Senior Board (reference) */}
        <div className="flex justify-center mb-4">
          <div className="px-6 py-2 rounded-full" style={{ backgroundColor: IP_STYLES.board.bg }}>
            <span className="text-sm font-semibold uppercase" style={{ color: IP_STYLES.board.text }}>
              National Senior Board
            </span>
          </div>
        </div>
        <div className="flex justify-center mb-4">
          <div className="w-0.5 h-6" style={{ backgroundColor: '#CBD5E1' }} />
        </div>

        {/* TIER 2: National C-Suite (reference) */}
        <div className="flex justify-center mb-4">
          <div className="px-6 py-2 rounded-full" style={{ backgroundColor: IP_STYLES.csuite.bg }}>
            <span className="text-sm font-semibold uppercase" style={{ color: IP_STYLES.csuite.text }}>
              National C-Suite
            </span>
          </div>
        </div>
        <div className="flex justify-center mb-4">
          <div className="w-0.5 h-6" style={{ backgroundColor: '#CBD5E1' }} />
        </div>

        {/* TIER 3: Soul Food Executive Director */}
        <div className="flex justify-center mb-4">
          <div className="px-4 py-1.5 rounded-full" style={{ backgroundColor: IP_STYLES.execDirector.bg }}>
            <span className="text-xs font-semibold uppercase" style={{ color: IP_STYLES.execDirector.text }}>
              Soul Food Executive Director
            </span>
          </div>
        </div>
        {uniqueIPData.executiveDirector && (
          <div className="flex justify-center mb-4">
            <RoleNode role={uniqueIPData.executiveDirector} style={IP_STYLES.execDirector} onClick={handleNodeClick} size="large" />
          </div>
        )}
        <div className="flex justify-center mb-4">
          <div className="w-0.5 h-6" style={{ backgroundColor: '#CBD5E1' }} />
        </div>

        {/* TIER 4: Functional Directors */}
        <div className="flex justify-center mb-4">
          <div className="px-4 py-1.5 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
            <span className="text-xs font-semibold uppercase" style={{ color: '#374151' }}>
              Functional Directors
            </span>
          </div>
        </div>
        <div className="flex justify-center gap-3 flex-wrap mb-4">
          {uniqueIPData.directors.map((director) => (
            <RoleNode 
              key={director.id} 
              role={director} 
              style={{ ...IP_STYLES.director, bg: director.ipDeptColor || '#E5E7EB', text: '#FFFFFF' }} 
              onClick={handleNodeClick}
              size="small"
            />
          ))}
        </div>
        <div className="flex justify-center mb-6">
          <div className="w-0.5 h-6" style={{ backgroundColor: '#CBD5E1' }} />
        </div>

        {/* TIER 5-7: Department Columns */}
        <div className="flex justify-center mb-4">
          <div className="px-4 py-1.5 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
            <span className="text-xs font-semibold uppercase" style={{ color: '#374151' }}>
              Department Structure
            </span>
          </div>
        </div>
        <div className="flex justify-center gap-4 flex-wrap">
          {uniqueIPData.departmentList.map((dept) => (
            <IPDepartmentColumn key={dept.name} dept={dept} onClick={handleNodeClick} />
          ))}
          {uniqueIPData.chiefOfStaff && (
            <ChiefOfStaffColumn cos={uniqueIPData.chiefOfStaff} onClick={handleNodeClick} />
          )}
        </div>
      </>
    );
  };

  // ========================
  // MAIN RENDER
  // ========================

  return (
    <>
      <div className="rounded-xl overflow-hidden relative" style={{ height: '750px', backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB' }} data-testid="org-chart-container">
        
        {/* Scope Selector - Only show when scopeFilter is not provided */}
        {!scopeFilter && (
          <div className="absolute top-4 left-16 z-10 bg-white rounded-lg shadow-md p-2">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4" style={{ color: '#6B7280' }} />
              <Select value={scope} onValueChange={setScope}>
                <SelectTrigger className="w-[140px] h-8 text-sm" data-testid="scope-selector">
                  <SelectValue placeholder="Select scope" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="national">National</SelectItem>
                  <SelectItem value="regional">Regional</SelectItem>
                  <SelectItem value="unique_ip">UNIQUE IP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Zoom Controls */}
        <div className="absolute top-4 left-4 z-10 flex flex-col gap-2 bg-white rounded-lg shadow-md p-2">
          <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0" title="Zoom In">
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0" title="Zoom Out">
            <ZoomOut className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={handleResetZoom} className="h-8 w-8 p-0" title="Reset Zoom">
            <Maximize2 className="w-4 h-4" />
          </Button>
          <div className="text-xs text-center" style={{ color: '#6B7280' }}>{Math.round(zoom * 100)}%</div>
        </div>

        {/* Legend */}
        <div className="absolute top-4 right-4 z-10 bg-white rounded-lg shadow-md p-3">
          <div className="text-xs font-medium mb-2" style={{ color: '#374151' }}>
            {scope === 'unique_ip' ? 'Soul Food Legend' : 'Legend'}
          </div>
          <div className="space-y-1.5">
            {scope === 'national' ? (
              [
                { label: 'Board', style: NATIONAL_TIER_STYLES[1] },
                { label: 'Executive', style: NATIONAL_TIER_STYLES[2] },
                { label: 'Directors', style: NATIONAL_TIER_STYLES[3] },
                { label: 'Regional', style: NATIONAL_TIER_STYLES[4] },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: item.style.nodeBg, border: `1px solid ${item.style.nodeBorder}` }} />
                  <span className="text-xs" style={{ color: '#6B7280' }}>{item.label}</span>
                </div>
              ))
            ) : (
              [
                { label: 'Executive Director', style: IP_STYLES.execDirector },
                { label: 'Director', style: { bg: '#6B7280', border: '#4B5563' } },
                { label: 'Head', style: IP_STYLES.head },
                { label: 'Lead', style: IP_STYLES.lead },
                { label: 'Team Member', style: IP_STYLES.teamMember },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded" style={{ backgroundColor: item.style.bg, border: `1px solid ${item.style.border}` }} />
                  <span className="text-xs" style={{ color: '#6B7280' }}>{item.label}</span>
                </div>
              ))
            )}
            <div className="flex items-center gap-2 pt-1 border-t" style={{ borderColor: '#E5E7EB' }}>
              <div className="w-3 h-3 rounded" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FCD34D' }} />
              <span className="text-xs" style={{ color: '#6B7280' }}>Vacant</span>
            </div>
          </div>
        </div>

        {/* Chart Content */}
        <ScrollArea className="h-full w-full">
          <div
            ref={containerRef}
            className="min-h-full py-8 px-4"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', minWidth: 'max-content' }}
          >
            {scope === 'national' && renderNationalChart()}
            {scope === 'regional' && (
              <div className="flex flex-col items-center justify-center h-[500px]">
                <p className="text-lg" style={{ color: '#6B7280' }}>
                  Please use the <strong>Regional</strong> tab for the Regional Organizational Chart
                </p>
              </div>
            )}
            {scope === 'unique_ip' && renderUniqueIPChart()}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Role Detail Drawer */}
      <RoleDetailDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setSelectedRole(null); }}
        role={selectedRole}
        departments={departments}
        people={people}
        onUpdate={handleUpdate}
      />
    </>
  );
};

export default OrgChart;
